import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useBlocker } from 'react-router-dom'
import { Button, buttonVariants } from '@/components/ui/button'
import { UnsavedChangesRegistry, type GuardState, type GuardRegistration } from '@/lib/unsaved-changes-policy'

interface PendingClose {
  // Omitted id protects every active editor, for actions such as signing out.
  id?: string
  action: () => void
}

interface UnsavedChangesContextValue {
  register: (id: string, registration: GuardRegistration) => () => void
  release: (id: string) => void
  confirmDiscard: (id: string | undefined, action: () => void) => void
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null)

/** One router blocker for all mounted editors, including editors inside dialogs. */
export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const guards = useRef(new UnsavedChangesRegistry())
  const [revision, setRevision] = useState(0)
  const [pendingClose, setPendingClose] = useState<PendingClose | null>(null)
  const restoreFocus = useRef<HTMLElement | null>(null)
  const keepEditingButton = useRef<HTMLButtonElement | null>(null)

  const register = useCallback((id: string, registration: GuardRegistration) => {
    const unregister = guards.current.register(id, registration)
    setRevision((value) => value + 1)
    return () => {
      unregister()
      setRevision((value) => value + 1)
    }
  }, [])

  const release = useCallback((id: string) => {
    guards.current.release(id)
    setRevision((value) => value + 1)
  }, [])

  const confirmDiscard = useCallback((id: string | undefined, action: () => void) => {
    if (!guards.current.needsWarning(id)) {
      action()
      return
    }
    restoreFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    // Do not replace an already pending action after repeated dismissal attempts.
    setPendingClose((current) => current ?? { id, action })
  }, [])

  const shouldBlock = useCallback(() => {
    return guards.current.needsWarning()
  }, [])
  const blocker = useBlocker(shouldBlock)
  const routeBlocked = blocker.state === 'blocked'
  const confirmationOpen = routeBlocked || pendingClose !== null
  const saving = !guards.current.canDiscard(routeBlocked ? undefined : pendingClose?.id)

  useEffect(() => {
    if (!shouldBlock()) return
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldBlock()) return
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warnBeforeUnload)
    return () => window.removeEventListener('beforeunload', warnBeforeUnload)
  }, [shouldBlock, revision])

  useEffect(() => {
    if (pendingClose?.id !== undefined && !guards.current.has(pendingClose.id)) setPendingClose(null)
  }, [pendingClose, revision])

  useEffect(() => {
    if (blocker.state === 'blocked' && !shouldBlock()) blocker.proceed()
  }, [blocker, revision, shouldBlock])

  const keepEditing = () => {
    setPendingClose(null)
    if (blocker.state === 'blocked') blocker.reset()
  }

  const discardChanges = () => {
    // Recheck at click time; a save may have started since the prompt opened.
    guards.current.discardAndContinue(() => {
      setRevision((value) => value + 1)
      const action = pendingClose?.action
      setPendingClose(null)
      if (blocker.state === 'blocked') blocker.proceed()
      else action?.()
    }, routeBlocked ? undefined : pendingClose?.id)
  }

  const context = useMemo(() => ({ register, release, confirmDiscard }), [register, release, confirmDiscard])

  return <UnsavedChangesContext.Provider value={context}>
    {children}
    <DialogPrimitive.Root open={confirmationOpen} onOpenChange={(open) => { if (!open) keepEditing() }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-slate-950/55 backdrop-blur-sm" />
        <DialogPrimitive.Content
          role="alertdialog"
          className="fixed left-1/2 top-1/2 z-[60] grid w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl border bg-background p-6 shadow-xl"
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => {
            event.preventDefault()
            if (!restoreFocus.current?.isConnected) {
              restoreFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
            }
            keepEditingButton.current?.focus()
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            if (restoreFocus.current?.isConnected) restoreFocus.current.focus()
            restoreFocus.current = null
          }}
        >
          <DialogPrimitive.Title className="text-lg font-semibold">
            {saving ? 'Save in progress' : 'Discard unsaved changes?'}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="text-sm text-muted-foreground">
            {saving
              ? 'Please wait for saving to finish before leaving. Your work is still open.'
              : 'Your changes have not been saved. Keep editing to preserve them, or discard them to continue.'}
          </DialogPrimitive.Description>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button ref={keepEditingButton} type="button" className={buttonVariants({ variant: 'outline' })} onClick={keepEditing}>Keep editing</button>
            {!saving && <Button type="button" variant="destructive" onClick={discardChanges}>Discard changes</Button>}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  </UnsavedChangesContext.Provider>
}

interface UnsavedChangesOptions {
  enabled?: boolean
  busy?: boolean
  onDiscard?: () => void
}

/**
 * Registers an explicit dirty editor. markSaved releases the guard synchronously
 * before successful-save navigation; also mark its form baseline clean. Failed
 * mutations must not call markSaved or reset the draft.
 */
export function useUnsavedChanges(dirty: boolean, { enabled = true, busy = false, onDiscard }: UnsavedChangesOptions = {}) {
  const context = useContext(UnsavedChangesContext)
  if (!context) throw new Error('useUnsavedChanges requires UnsavedChangesProvider')
  const id = useId()
  const [registrationGeneration, setRegistrationGeneration] = useState(0)
  const state = useRef<GuardState>({ dirty, busy, onDiscard })
  state.current = { dirty, busy, onDiscard }

  useLayoutEffect(() => {
    if (!enabled) return
    return context.register(id, {
      current: () => state.current,
      release: () => {
        state.current = { ...state.current, dirty: false, busy: false }
        // Discard may replace one dirty value with another in the same render.
        // Rearm this mounted editor even when the dirty/busy booleans stay equal.
        setRegistrationGeneration((generation) => generation + 1)
      },
    })
  }, [context, id, enabled, dirty, busy, registrationGeneration])

  const confirmDiscard = useCallback((action: () => void) => {
    context.confirmDiscard(id, action)
  }, [context, id])
  const markSaved = useCallback(() => context.release(id), [context, id])
  return { confirmDiscard, markSaved }
}


/** Confirm leaving all active editors before non-router actions unmount the app. */
export function useConfirmLeave() {
  const context = useContext(UnsavedChangesContext)
  if (!context) throw new Error('useConfirmLeave requires UnsavedChangesProvider')
  return useCallback((action: () => void) => {
    context.confirmDiscard(undefined, action)
  }, [context])
}
